import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { UserService } from '../../service';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { ICompanyGroupOption, ICompanyUserGroup } from '../../dto/ICompanyUserGroup';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { IAllStates } from '../../../../../store/rootReducer';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import Chip from '../../../../../shared/components/chip/Chip';
import { useGroupOptions } from '../../../../../shared/hooks/useGroupOptions';

interface UserManageGroupsModalProps {
    show: boolean;
    closeModal: () => void;
    clientId?: number | string;
    userId?: number | string;
    setUpdateList?: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserManageGroupsModal = ({ show, closeModal, userId, clientId, setUpdateList }: UserManageGroupsModalProps) => {
    const { t } = useTranslation();
    const locale = useSelector((state: IAllStates) => state.auth.user.locale);
    const [allGroups, setAllGroups] = useState<ICompanyUserGroup[]>([]);
    const isDe = locale === 'de';
    const {
        selectedOptionsMap,
        autocompleteKeys,
        flatOptionIds,
        getAvailableOptions,
        handleOptionSelect,
        handleOptionRemove,
        initSelectedOptions,
        resetOptions,
    } = useGroupOptions();

    useEffect(() => {
        if (!show) return;
        UserService.getGroups(!!clientId, clientId, userId)
            .then(response => {
                setAllGroups(response);
                initSelectedOptions(response);
            })
            .catch(err => ErrorToast(err));
    }, [show]);

    function closeModalAction() {
        resetOptions();
        closeModal?.();
    }

    const addOptionsApiRequest = () => {
        UserService.addGroup({ optionIds: flatOptionIds }, userId)
            .then(() => {
                SuccessToast('Successfully added options');
                closeModalAction();
                setUpdateList?.(prev => !prev);
            })
            .catch(err => ErrorToast(err));
    };

    function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        addOptionsApiRequest();
    }

    return (
        <Modal show={show} onHide={closeModalAction} size="lg">
            <form onSubmit={onSubmit}>
                <Modal.Header>
                    <Modal.Title>
                        <h4 className="m-0">{t('brand.account.assign_user_groups')}</h4>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="px-4 overflow-auto" style={{ maxHeight: '75vh' }}>
                    {allGroups.length === 0 ? (
                        <EmptyState />
                    ) : (
                        allGroups?.map(group => {
                            const selectedForGroup = selectedOptionsMap[group.id] ?? [];
                            const availableOptions = getAvailableOptions(group);

                            // const isDisabled = !group.isMultiple && selectedForGroup.length >= 1;

                            return (
                                <div className="mb-4" key={group.id}>
                                    <h6>
                                        {isDe ? group.nameDe : group.name}{' '}
                                        {group?.isMultiple && <span className="text-muted"> (multiple select)</span>}
                                    </h6>
                                    <CustomAutocomplete
                                        key={autocompleteKeys[group.id] ?? 0}
                                        dataArray={availableOptions}
                                        isWithImage={false}
                                        inputLabel="options"
                                        isMultiple={false}
                                        queryFilterKey={`group-${group.id}`}
                                        inputLabelTranslation={`Search by ${isDe ? group.nameDe : group.name}`}
                                        handleChangeParams={(_key, paramValue) => {
                                            if (paramValue)
                                                handleOptionSelect(group, paramValue as unknown as ICompanyGroupOption);
                                        }}
                                        usePortal
                                        popperZIndex={2000}
                                    />
                                    {selectedForGroup.length > 0 && (
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            {selectedForGroup.map(option => (
                                                <Chip
                                                    key={option?.id}
                                                    label={isDe ? option.labelDe : option.label}
                                                    onDelete={() => handleOptionRemove(group.id, option.id)}
                                                    isActive={true}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-primary" onClick={closeModal}>
                        {t('general.button.close')}
                    </Button>
                    <Button type="submit" variant="primary" className="btn-sm">
                        {t('general.button.save')}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default React.memo(UserManageGroupsModal);
